import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { processSteps } from "@/mocks/process";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const { t } = useTranslation();

  const timeline = [
    { phase: t("hiw.phaseMatch"), weeks: t("hiw.phaseMatchWeeks"), desc: t("hiw.phaseMatchDesc") },
    { phase: t("hiw.phaseInspect"), weeks: t("hiw.phaseInspectWeeks"), desc: t("hiw.phaseInspectDesc") },
    { phase: t("hiw.phasePurchase"), weeks: t("hiw.phasePurchaseWeeks"), desc: t("hiw.phasePurchaseDesc") },
    { phase: t("hiw.phaseRenovate"), weeks: t("hiw.phaseRenovateWeeks"), desc: t("hiw.phaseRenovateDesc") },
    { phase: t("hiw.phaseLaunch"), weeks: t("hiw.phaseLaunchWeeks"), desc: t("hiw.phaseLaunchDesc") },
    { phase: t("hiw.phaseOperate"), weeks: t("hiw.phaseOperateWeeks"), desc: t("hiw.phaseOperateDesc") },
  ];

  return (
    <div className="min-h-screen bg-paper">
      <Navbar />
      <main className="pt-24 md:pt-32 pb-20">
        <div className="w-full px-4 md:px-8 lg:px-12 mb-16 md:mb-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-4">{t("hiw.sectionLabel")}</p>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ink mb-6 leading-tight">{t("hiw.title")}</h1>
            <p className="text-lg text-ink/50 font-light max-w-2xl leading-relaxed">{t("hiw.intro")}</p>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 lg:px-12 mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, idx) => (
              <div key={step.step}
                onClick={() => setActiveStep(activeStep === idx ? null : idx)}
                className={`bg-white border border-hairline p-6 md:p-8 cursor-pointer transition-all duration-300 ${activeStep === idx ? "border-ink/30 -translate-y-1" : "hover:border-ink/20"}`}>
                <div className="flex items-start justify-between mb-5">
                  <span className="font-serif text-3xl font-light text-gold">{step.step}</span>
                  <i className={`ri-arrow-down-s-line text-ink/30 transition-transform duration-300 ${activeStep === idx ? "rotate-180" : ""}`}></i>
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink mb-3">{step.title}</h3>
                <p className="text-sm text-ink/50 leading-relaxed font-light">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full px-4 md:px-8 lg:px-12 mb-20">
          <div className="bg-ink p-8 md:p-12 relative overflow-hidden">
            <div className="relative z-10">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-8">{t("hiw.timelineLabel")}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                {timeline.map((t2) => (
                  <div key={t2.phase} className="text-center">
                    <p className="text-xs text-paper/40 mb-1">{t2.weeks}</p>
                    <p className="font-serif text-lg text-paper mb-1">{t2.phase}</p>
                    <p className="text-xs text-paper/30">{t2.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ink mb-4">{t("hiw.ctaTitle")}</h2>
            <p className="text-ink/50 font-light mb-8">{t("hiw.ctaDesc")}</p>
            <Link to="/contact"
              className="inline-flex items-center gap-2 bg-ink text-paper text-sm font-medium px-8 py-3.5 rounded-full hover:bg-indigo transition-colors duration-300 whitespace-nowrap">
              {t("btn.requestList")}
              <i className="ri-arrow-right-up-line text-sm"></i>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}